import React, { useState, useRef, useEffect } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CForm,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CAlert,
  CSpinner,
} from "@coreui/react";
import axios from "axios";

const AddTypes = ({
  visible,
  onClose,
  onAddSuccess,
  isEditMode = false,
  editData = null,
}) => {
  const [objectName, setObjectName] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("0");
  const [iconPreview, setIconPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({
    show: false,
    text: "",
    color: "info",
  });
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("token");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Список защищенных объектов
  const protectedObjectNames = [
    "Опора ВЛ",
    "Подстанция",
    "Здание",
    "Мачта",
    "Башня",
  ];

  const typeOptions = [
    { id: "0", name: "Линии ВЛ" },
    { id: "1", name: "Подстанция" },
    { id: "2", name: "Здания" },
    { id: "3", name: "АМС" },
  ];

  useEffect(() => {
    if (isEditMode && editData && visible) {
      setObjectName(editData.ObjectName || "");
      setSelectedTypeId(editData.ObjectCType?.toString() || "0");
      if (editData.ObjectIcon) {
        const fullBase64String = editData.ObjectIcon.startsWith("data:image")
          ? editData.ObjectIcon
          : `data:image/png;base64,${editData.ObjectIcon}`;
        setIconPreview(fullBase64String);
        setSelectedFile(null);
      } else {
        setIconPreview(null);
        setSelectedFile(null);
      }
    } else if (!isEditMode && visible) {
      setObjectName("");
      setSelectedTypeId("0");
      setIconPreview(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    setMessage({ show: false, text: "", color: "info" });
  }, [isEditMode, editData, visible]);

  const handleIconClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "image/png") {
        setMessage({
          show: true,
          text: "Пожалуйста, выберите файл формата PNG.",
          color: "danger",
        });
        setTimeout(
          () => setMessage({ show: false, text: "", color: "info" }),
          3000,
        );
        return;
      }
      const maxSizeInBytes = 2 * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        setMessage({
          show: true,
          text: "Файл слишком большой. Максимальный размер 2MB.",
          color: "danger",
        });
        setTimeout(
          () => setMessage({ show: false, text: "", color: "info" }),
          3000,
        );
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result);
        setSelectedFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!objectName.trim()) {
      setMessage({
        show: true,
        text: "Пожалуйста, введите наименование сооружения.",
        color: "warning",
      });
      setTimeout(
        () => setMessage({ show: false, text: "", color: "info" }),
        3000,
      );
      return;
    }

    setLoading(true);
    setMessage({ show: false, text: "", color: "info" });

    try {
      let base64IconString = null;

      if (selectedFile) {
        base64IconString = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64Data = reader.result.split(",")[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(selectedFile);
        });
      } else if (isEditMode && iconPreview && !selectedFile) {
        if (iconPreview.startsWith("data:image")) {
          base64IconString = iconPreview.split(",")[1];
        } else {
          base64IconString = iconPreview;
        }
      }

      let determinedObjectType = null;
      let objectTypeUpdated = false;

      if (!isEditMode) {
        try {
          const searchResponse = await axios.get(`/api/catalog/`, {
            params: { ntype: 33, name: objectName },
            headers: { Authorization: `Bearer ${token}` },
          });
          if (
            searchResponse.data &&
            Array.isArray(searchResponse.data) &&
            searchResponse.data.length > 0
          ) {
            const foundItem = searchResponse.data.find(
              (item) =>
                item.Name &&
                item.Name.trim().toLowerCase() ===
                  objectName.trim().toLowerCase(),
            );
            if (foundItem) {
              determinedObjectType = foundItem.Id;
              console.log(
                `Найден существующий тип в каталоге с ID: ${determinedObjectType}`,
              );
            }
          }
          if (determinedObjectType === null) {
            console.log("Тип не найден, создаем новый в каталоге...");
            const createCatalogResponse = await axios.post(
              `/api/catalog/`,
              {
                Name: objectName,
                NType: 33,
                NValue: 1,
                NParam: "",
                idParent: 0,
                Field1: "",
                Field2: "",
                Field3: "",
                Field4: "",
                Field5: "",
                Field6: "",
              },
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              },
            );
            if (
              createCatalogResponse.status === 200 ||
              createCatalogResponse.status === 201
            ) {
              const recheckResponse = await axios.get(`/api/catalog/`, {
                params: { ntype: 33, name: objectName },
                headers: { Authorization: `Bearer ${token}` },
              });
              if (
                recheckResponse.data &&
                Array.isArray(recheckResponse.data) &&
                recheckResponse.data.length > 0
              ) {
                const recheckedItem = recheckResponse.data.find(
                  (item) =>
                    item.Name &&
                    item.Name.trim().toLowerCase() ===
                      objectName.trim().toLowerCase(),
                );
                if (recheckedItem) {
                  determinedObjectType = recheckedItem.Id;
                  console.log(
                    `Создан и найден новый тип в каталоге с ID: ${determinedObjectType}`,
                  );
                }
              }
            }
          }
        } catch (catalogError) {
          console.error(
            "Ошибка работы с каталогом при создании:",
            catalogError,
          );
          setMessage({
            show: true,
            text: "Ошибка при работе с каталогом при создании.",
            color: "warning",
          });
          setTimeout(
            () => setMessage({ show: false, text: "", color: "info" }),
            5000,
          );
        }
      } else {
        if (
          editData &&
          editData.ObjectType &&
          editData.ObjectName !== objectName
        ) {
          try {
            console.log(
              `Обновляем имя в каталоге с ID: ${editData.ObjectType} на '${objectName}'`,
            );
            const updateCatalogResponse = await axios.put(
              `/api/catalog/`,
              {
                Id: editData.ObjectType,
                Name: objectName,
                NType: 33,
                NValue: 1,
                NParam: "",
                idParent: 0,
                Field1: "",
                Field2: "",
                Field3: "",
                Field4: "",
                Field5: "",
                Field6: "",
              },
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              },
            );

            if (updateCatalogResponse.status === 200) {
              console.log(
                `Имя в каталоге с ID ${editData.ObjectType} успешно обновлено.`,
              );
              determinedObjectType = editData.ObjectType;
              objectTypeUpdated = true;
            } else {
              console.warn(
                `Неожиданный статус при обновлении каталога: ${updateCatalogResponse.status}`,
              );
              determinedObjectType = editData.ObjectType;
            }
          } catch (updateCatalogError) {
            console.error(
              "Ошибка обновления имени в каталоге:",
              updateCatalogError,
            );
            setMessage({
              show: true,
              text: "Предупреждение: Имя в связанном каталоге не было обновлено.",
              color: "warning",
            });
            setTimeout(
              () => setMessage({ show: false, text: "", color: "info" }),
              5000,
            );
            determinedObjectType = editData.ObjectType;
          }
        } else {
          determinedObjectType = editData?.ObjectType ?? null;
        }
      }

      let apiData;
      if (isEditMode) {
        apiData = {
          ObjectID: editData.ObjectID,
          ObjectName: objectName,
          Description: editData.Description || "",
          ObjectType: determinedObjectType,
          ObjectCType: parseInt(selectedTypeId, 10),
          ObjectIcon: base64IconString,
        };
      } else {
        apiData = {
          ObjectName: objectName,
          Description: "",
          ObjectType: determinedObjectType,
          ObjectCType: parseInt(selectedTypeId, 10),
          ObjectIcon: base64IconString,
        };
      }

      console.log(
        `${isEditMode ? "Отправляемые данные для обновления" : "Отправляемые данные для создания"}:`,
        apiData,
      );

      let response;
      if (isEditMode) {
        response = await axios.put(
          `/api/neural_network_module/objects/`,
          apiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        response = await axios.post(
          `/api/neural_network_module/objects/`,
          apiData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
      }

      console.log("Ответ от API объектов:", response.data);
      const successMessage = isEditMode
        ? objectTypeUpdated
          ? "Тип объекта и связанная запись в каталоге успешно обновлены!"
          : "Тип объекта успешно обновлен!"
        : "Новый тип объекта успешно добавлен!";
      setMessage({ show: true, text: successMessage, color: "success" });

      setTimeout(() => {
        onAddSuccess();
        handleClose();
      }, 1500);
    } catch (error) {
      console.error(
        `${isEditMode ? "Ошибка при обновлении" : "Ошибка при добавлении"} типа объекта:`,
        error,
      );
      let errorMessage = isEditMode
        ? "Произошла ошибка при обновлении типа объекта."
        : "Произошла ошибка при добавлении типа объекта.";
      if (error.response) {
        console.error("Данные ошибки от сервера:", error.response.data);
        console.error("Статус ошибки:", error.response.status);
        console.error("Заголовки ошибки:", error.response.headers);
        errorMessage += ` (Сервер: ${error.response.status})`;
        if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (
          error.response.data &&
          typeof error.response.data === "string"
        ) {
          errorMessage = error.response.data;
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.request) {
        console.error("Запрос без ответа:", error.request);
        errorMessage += " (Нет ответа от сервера)";
      } else {
        console.error("Ошибка настройки запроса:", error.message);
      }
      setMessage({ show: true, text: errorMessage, color: "danger" });
    } finally {
      setLoading(false);
      if (message.show) {
        setTimeout(
          () => setMessage({ show: false, text: "", color: "info" }),
          5000,
        );
      }
    }
  };

  const handleClose = () => {
    setObjectName("");
    setSelectedTypeId("0");
    setIconPreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setMessage({ show: false, text: "", color: "info" });
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAction = async () => {
    setShowDeleteConfirm(false);
    if (!isEditMode || !editData) {
      console.error("Ошибка: Нет данных для удаления.");
      return;
    }

    if (protectedObjectNames.includes(editData.ObjectName)) {
      console.warn(`Объект "${editData.ObjectName}" защищен от удаления.`);
      return;
    }

    try {
      try {
        await axios.delete(`/api/neural_network_module/combinedtable/`, {
          params: { ObjectID: editData.ObjectID }, // Используем params для query-параметров
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log(
          `Связанные записи для объекта ${editData.ObjectID} удалены (или попытка удаления выполнена).`,
        );
      } catch (combinedTableDeleteError) {
        console.warn(
          `Возможно, не удалось удалить связанные записи из combined-table для объекта ${editData.ObjectID}:`,
          combinedTableDeleteError,
        );
      }

      const response = await axios.delete(
        `/api/neural_network_module/objects/`,
        {
          params: { ObjectID: editData.ObjectID }, // Используем params для query-параметров
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status >= 200 && response.status < 300) {
        console.log(`Объект ${editData.ObjectName} успешно удален.`);

        onAddSuccess(); // Вызываем onAddSuccess, чтобы обновить список в SettingsNeuralModule
        handleClose(); // Закрываем это модальное окно AddTypes
      } else {
        console.error(
          `Неожиданный статус ответа при удалении объекта: ${response.status}`,
        );
      }
    } catch (error) {
      console.error("Ошибка при удалении объекта:", error);
    }
  };

  // Стили для ограничения размера модального окна
  const modalStyle = {
    maxHeight: "auto",
    maxWight: "50vw",
    display: "flex",
    flexDirection: "column",
  };
  // Стили для ограничения размера модального окна удаления
  const modalStyleDelete = {
    maxHeight: "25vh",
    maxWight: "50vw",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <>
      <CModal
        alignment="start"
        size="lg"
        visible={visible}
        onClose={handleClose}
        backdrop="static"
        style={modalStyle}
      >
        <CModalHeader>
          <CModalTitle>
            {isEditMode
              ? "Редактировать тип объекта"
              : "Добавить новый тип объекта"}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {message.show && (
            <CAlert color={message.color} className="mb-3">
              {message.text}
            </CAlert>
          )}
          <CForm>
            <div className="mb-3">
              <CFormLabel>Наименование сооружения*:</CFormLabel>
              <CFormInput
                type="text"
                value={objectName}
                onChange={(e) => setObjectName(e.target.value)}
                placeholder="Введите наименование"
                disabled={loading}
              />
              <small className="text-muted">* - обязательное поле</small>
            </div>

            <div className="mb-3">
              <CFormLabel>Тип объекта:</CFormLabel>
              <CFormSelect
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                disabled={loading}
              >
                {typeOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </CFormSelect>
            </div>

            <div className="mb-3">
              <CFormLabel>Значок:</CFormLabel>
              <div
                onClick={handleIconClick}
                style={{
                  width: "400px",
                  height: "400px",
                  border: "1px dashed #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  overflow: "hidden",
                  backgroundColor: "#f8f9fa",
                  margin: "0 auto",
                  marginBottom: "10px",
                }}
              >
                {iconPreview ? (
                  <img
                    src={iconPreview}
                    alt="Превью"
                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                  />
                ) : (
                  <span style={{ color: "#6c757d" }}>Выбрать PNG</span>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".png"
                style={{ display: "none" }}
                disabled={loading}
              />
              <small className="text-muted d-block text-center">
                PNG (рекомендуется 512х512)
              </small>
            </div>
          </CForm>
        </CModalBody>
        <CModalFooter>
          {isEditMode &&
            !protectedObjectNames.includes(editData?.ObjectName) && (
              <CButton color="danger" onClick={handleDeleteClick}>
                Удалить
              </CButton>
            )}
          <CButton color="secondary" onClick={handleClose} disabled={loading}>
            Отмена
          </CButton>
          <CButton color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <CSpinner
                  component="span"
                  size="sm"
                  aria-hidden="true"
                  className="me-2"
                />
                {isEditMode ? "Обновление..." : "Добавление..."}
              </>
            ) : isEditMode ? (
              "Обновить"
            ) : (
              "ОК"
            )}
          </CButton>
        </CModalFooter>
      </CModal>
      <CModal
        alignment="start"
        size="lg"
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        style={modalStyleDelete}
      >
        <CModalHeader>
          <CModalTitle>Подтверждение удаления</CModalTitle>
        </CModalHeader>
        <CModalBody>
          Вы уверены, что хотите удалить объект "{editData?.ObjectName}" и все
          связанные с ним записи?
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Отмена
          </CButton>
          <CButton color="danger" onClick={confirmDeleteAction}>
            Удалить
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default AddTypes;
